import { getDownloadURL, ref, uploadBytes, uploadBytesResumable } from 'firebase/storage';
import { storage } from '@/firebaseInit';
import { VIDEO_TYPE_EXTENSIONS } from '@/util/util_heroVideo';

// Uploads admin-picked media to Firebase Storage and returns its public
// download URL — this is what gets stored on the document (heroImage /
// galleryImages / videoUrl / a guide's photo), same as if an editor had pasted
// an external URL.
//
// The folder is part of the signature rather than the caller's business,
// because storage.rules grants write per top-level folder: an upload into a
// folder with no rule is refused, so the set here and the set there are one
// thing in two files.
export type StorageFolderType = 'experiences' | 'guides';

/**
 * Video MIME types the file picker offers and `uploadVideo` accepts. Derived
 * from `VIDEO_TYPE_EXTENSIONS` rather than written out again, so what can be
 * uploaded and what `parseHeroVideoSource` will later recognise are the same
 * set by construction.
 */
export const ACCEPTED_VIDEO_TYPES = Object.keys(VIDEO_TYPE_EXTENSIONS);

/**
 * Ceiling on a hero video, in bytes.
 *
 * Not a technical limit — Storage would take a 500MB file happily. It is a bill
 * and a first impression: the hero autoplays for every visitor to the page, so
 * every megabyte here is egress paid per view and time the poster sits there
 * instead of the video. A 10–15s loop at 1080p lands well under this once the
 * audio track is stripped; a file that doesn't needs re-encoding, not a bigger
 * limit.
 *
 * Enforced again in storage.rules — this check is the one that can explain
 * itself, that one is the one that cannot be skipped. Change both together.
 */
export const MAX_VIDEO_BYTES = 25 * 1024 * 1024;

/** Human-readable size, for the message shown when a file is turned away. */
export function formatBytes(bytes: number): string {
  return `${(bytes / (1024 * 1024)).toFixed(1)}MB`;
}

/**
 * A file refused before it left the browser — too big, or a format we can't
 * play. Its own type rather than a plain Error because the wording is written
 * for an editor and says what to fix, so the UI can show it verbatim; every
 * other failure here is a Firebase `StorageError`, whose message ("Firebase
 * Storage: User does not have permission… (storage/unauthorized)") is not.
 */
export class MediaRejectedError extends Error {
  override name = 'MediaRejectedError';
}

// Uploaded objects are never overwritten — the path carries a fresh uuid every
// time — so they can be cached for as long as any browser is willing to.
const IMMUTABLE = 'public, max-age=31536000, immutable';

function storagePath(folder: StorageFolderType, name: string): string {
  return `${folder}/${crypto.randomUUID()}-${name}`;
}

/**
 * Longest edge, in pixels, of a stored image.
 *
 * Every image an editor picks is downscaled to this before it is uploaded — a
 * photo straight off a phone is 4000px and several megabytes, and every one of
 * those bytes is paid for on every view of a page that shows it at 1200px wide,
 * or at 96px in a card. 2000px still covers the widest place any image is used
 * (a full-bleed hero on a 2x laptop display) with nothing left over.
 *
 * Images are never scaled *up*: something smaller than this is already smaller
 * than we would make it, and enlarging it would only add bytes and blur.
 */
export const MAX_IMAGE_EDGE = 2000;

/**
 * Ceiling on the *source* file an editor picks, before it is resized.
 *
 * Nothing reaches Storage at this size — the upload is a few hundred KB of JPEG
 * by then, so storage.rules never sees the original and cannot be the thing that
 * refuses it. This is the check that keeps a browser tab from spending its
 * memory decoding a 200MP scan, so it lives here or nowhere.
 */
export const MAX_IMAGE_SOURCE_BYTES = 30 * 1024 * 1024;

// Everything is re-encoded to WebP: one format for one job, so a 6MB PNG
// screenshot and a 12MB phone photo both land as a couple of hundred KB, and
// nothing downstream has to care what an editor happened to pick. WebP is ~30%
// smaller than JPEG at the same visible quality, decodes everywhere we support,
// and — unlike JPEG — keeps an alpha channel, so a logo with a transparent
// background survives the trip.
const WEBP = { type: 'image/webp', extension: '.webp' } as const;

// The fallback, for a browser whose canvas cannot *encode* WebP. Decoding it is
// universal; encoding was the later arrival, and `toBlob` handles an encoder it
// lacks by quietly writing a PNG instead — a 2000px PNG photo being several MB,
// exactly the thing this whole function exists to prevent. So the format is
// decided by asking, once, rather than by hoping.
const JPEG = { type: 'image/jpeg', extension: '.jpg' } as const;

// Chosen by eye on photographs at this size: below ~0.75 the sky in a landscape
// starts to band, above ~0.9 the file grows for detail no visitor sees.
const IMAGE_QUALITY = 0.82;

let webpEncoder: boolean | null = null;

/**
 * Whether this browser's canvas can encode WebP, asked once and remembered.
 *
 * `toDataURL` reports the type it actually produced, which is the only honest
 * answer available: a canvas asked for a format it doesn't have returns a PNG
 * under its own MIME type rather than failing.
 */
function canEncodeWebp(): boolean {
  if (webpEncoder === null) {
    const probe = document.createElement('canvas');
    probe.width = 1;
    probe.height = 1;
    webpEncoder = probe.toDataURL(WEBP.type).startsWith(`data:${WEBP.type}`);
  }
  return webpEncoder;
}

/** The picked file's name, re-pointed at the format it is actually stored in. */
function storedName(name: string, extension: string): string {
  const stem = name.replace(/\.[^./\\]+$/, '');
  return `${stem || 'image'}${extension}`;
}

/**
 * Downscales an editor's image to `MAX_IMAGE_EDGE` and re-encodes it as WebP,
 * returning a file to upload in place of the original.
 *
 * Done in the browser rather than in a Storage-triggered function because the
 * URL written to the document is the URL of the object that was uploaded: resize
 * afterwards and either the document points at the original until a second write
 * catches up, or the resized copy has to take over the original's path. Neither
 * is worth the trouble when the decode is a few hundred milliseconds here — and
 * doing it first means the editor's upload is the small file too.
 *
 * One thing an editor should know: an animated GIF is stored as its first frame.
 * That is fine for catalogue photography, which is all this field has ever held;
 * the hero video field is where motion belongs.
 */
async function resizeImage(file: File): Promise<File> {
  // SVG is passed through untouched. `createImageBitmap` refuses it in Chrome
  // (an SVG has no intrinsic pixel size to decode to), and rasterising a logo to
  // fixed pixels would be a downgrade even where it works.
  if (file.type === 'image/svg+xml') return file;

  if (file.size > MAX_IMAGE_SOURCE_BYTES) {
    throw new MediaRejectedError(
      `Image is ${formatBytes(file.size)} — the limit is ${formatBytes(MAX_IMAGE_SOURCE_BYTES)}.`,
    );
  }

  let bitmap: ImageBitmap;
  try {
    // `from-image` applies the EXIF orientation tag while decoding. Without it a
    // photo taken in portrait on a phone decodes sideways, and — because the
    // canvas then bakes those pixels — would be stored sideways for good.
    bitmap = await createImageBitmap(file, { imageOrientation: 'from-image' });
  } catch {
    throw new MediaRejectedError(
      `${file.name} could not be read as an image. A HEIC photo from an iPhone needs exporting as JPEG first.`,
    );
  }

  try {
    const format = canEncodeWebp() ? WEBP : JPEG;

    const scale = Math.min(1, MAX_IMAGE_EDGE / Math.max(bitmap.width, bitmap.height));
    const width = Math.max(1, Math.round(bitmap.width * scale));
    const height = Math.max(1, Math.round(bitmap.height * scale));

    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;

    const context = canvas.getContext('2d');
    if (!context) throw new MediaRejectedError('This browser could not process the image. Please try again.');

    // WebP carries the alpha channel through, so a transparent PNG stays
    // transparent and nothing needs painting underneath it. On the JPEG
    // fallback there is no alpha to carry, and a canvas starts out transparent
    // *black* — so that path fills white first, the colour of the page behind
    // every image on the site, rather than storing the logo on a black square.
    if (format === JPEG) {
      context.fillStyle = '#ffffff';
      context.fillRect(0, 0, width, height);
    }
    context.imageSmoothingQuality = 'high';
    context.drawImage(bitmap, 0, 0, width, height);

    const blob = await new Promise<Blob | null>((resolve) => canvas.toBlob(resolve, format.type, IMAGE_QUALITY));
    if (!blob) throw new MediaRejectedError('This browser could not process the image. Please try again.');

    return new File([blob], storedName(file.name, format.extension), { type: format.type });
  } finally {
    // The decoded bitmap is width × height × 4 bytes of memory — 100MB for a
    // 24MP photo — held until it is closed, and the gallery field decodes
    // several at once.
    bitmap.close();
  }
}

export async function uploadImage(file: File, folder: StorageFolderType): Promise<string> {
  const resized = await resizeImage(file);
  const snapshot = await uploadBytes(ref(storage, storagePath(folder, resized.name)), resized, {
    contentType: resized.type,
    cacheControl: IMMUTABLE,
  });
  return getDownloadURL(snapshot.ref);
}

export function uploadExperienceImage(file: File): Promise<string> {
  return uploadImage(file, 'experiences');
}

/**
 * Uploads a hero video, reporting progress from 0 to 1.
 *
 * Resumable rather than the one-shot `uploadBytes` the images use: these files
 * are three orders of magnitude larger, so the editor needs to see something
 * moving, and a dropped connection retries from where it stopped instead of
 * from zero.
 *
 * Rejects an oversized or wrong-typed file here rather than only in the UI, so
 * the rule holds wherever it is called from.
 */
export function uploadVideo(
  file: File,
  folder: StorageFolderType,
  onProgress?: (fraction: number) => void,
): Promise<string> {
  const extension = VIDEO_TYPE_EXTENSIONS[file.type];
  if (!extension) {
    return Promise.reject(
      new MediaRejectedError(`${file.type || 'That file'} is not a supported video format — upload an MP4 or WebM.`),
    );
  }
  if (file.size > MAX_VIDEO_BYTES) {
    return Promise.reject(
      new MediaRejectedError(`Video is ${formatBytes(file.size)} — the limit is ${formatBytes(MAX_VIDEO_BYTES)}.`),
    );
  }

  // The stored name always ends in the extension for its type, whatever the
  // editor's file was called: the URL is read back by extension, so a file
  // named `hero-loop` or `clip.m4v` would upload happily and then be
  // unrecognisable as a video for the rest of its life.
  const name = file.name.toLowerCase().endsWith(extension) ? file.name : `${file.name}${extension}`;

  const task = uploadBytesResumable(ref(storage, storagePath(folder, name)), file, {
    contentType: file.type,
    cacheControl: IMMUTABLE,
  });

  return new Promise((resolve, reject) => {
    task.on(
      'state_changed',
      (snapshot) => onProgress?.(snapshot.totalBytes ? snapshot.bytesTransferred / snapshot.totalBytes : 0),
      reject,
      () => resolve(getDownloadURL(task.snapshot.ref)),
    );
  });
}

export function uploadExperienceVideo(file: File, onProgress?: (fraction: number) => void): Promise<string> {
  return uploadVideo(file, 'experiences', onProgress);
}
